/**
 * @fileoverview Blocking and advisory checks for the WIP import review screen.
 *
 * Pure, and deliberately kept out of the component: this list is the safety
 * mechanism of the whole import. Nothing is written to the firm's settings
 * while `collectBlockers` returns anything, so the rules are easier to audit
 * as a flat set than threaded through render logic.
 *
 * A blocker is something that would make the commit write a wrong number.
 * An advisory is something odd about the file that phase 1 does not act on.
 */

import type { WipAnalysis } from "@cvx/timesheets/import/wipAnalysis";
import { formatAudExact } from "../format";

/** One review row's resolved choice: an existing record's id, or null. */
export type MatchSelection = { label: string; selectedId: string | null };

export type ReviewSelections = {
  grades: MatchSelection[];
  staff: MatchSelection[];
};

/** Groups of labels that were pointed at the same existing record. */
function clashingSelections(selections: MatchSelection[]): string[][] {
  const labelsById = new Map<string, string[]>();
  for (const selection of selections) {
    if (selection.selectedId === null) continue;
    const labels = labelsById.get(selection.selectedId) ?? [];
    labels.push(selection.label);
    labelsById.set(selection.selectedId, labels);
  }
  return [...labelsById.values()].filter((labels) => labels.length > 1);
}

function join(parts: string[]): string {
  return parts.join(", ");
}

function rows(count: number): string {
  return `${count.toLocaleString("en-AU")} row${count === 1 ? "" : "s"}`;
}

/**
 * Problems that must be resolved before the configuration can be applied.
 * Every message names the evidence, because "something is wrong with your
 * spreadsheet" leaves the user with nowhere to go.
 */
export function collectBlockers(
  analysis: WipAnalysis,
  selections: ReviewSelections,
): string[] {
  const out: string[] = [];

  if (analysis.missingRequiredColumns.length > 0) {
    out.push(
      "This file is missing columns the import needs: " +
        `${join(analysis.missingRequiredColumns)}.`,
    );
  }

  if (analysis.dataRowCount === 0) {
    out.push("No data rows were recognised in this file.");
  }

  if (analysis.caseNames.length > 1) {
    out.push(
      `This file covers ${analysis.caseNames.length} matters ` +
        `(${join(analysis.caseNames)}). Import one matter's WIP at a time.`,
    );
  }

  const unitMinutes = analysis.unit.minutes;
  if (unitMinutes === null) {
    out.push(
      "The billing increment is not consistent across the file — " +
        analysis.unit.distribution
          .map((d) => `${d.minutes} minutes on ${rows(d.rows)}`)
          .join(", ") +
        ". One increment has to hold for the whole file.",
    );
  } else if (
    !Number.isInteger(unitMinutes) ||
    unitMinutes < 1 ||
    unitMinutes > 60
  ) {
    out.push(
      `The billing increment works out to ${unitMinutes} minutes, which ` +
        "Morgan cannot store — it holds whole minutes from 1 to 60.",
    );
  }

  // Imported rates are dated from the earliest entry. Falling back to today
  // would quietly date this period's rates outside it, and the error would
  // only surface much later, in a remuneration report.
  if (analysis.dateRange === null) {
    out.push(
      `No entry date could be read — ${analysis.dateFormatEvidence}. ` +
        "Imported rates are dated from the earliest entry in the file, so at " +
        "least one readable date is needed.",
    );
  }

  if (analysis.gradeRateConflicts.length > 0) {
    out.push(
      "These grades carry more than one hourly rate, so there is no single " +
        "rate to import: " +
        analysis.gradeRateConflicts
          .map(
            (conflict) =>
              `${conflict.name} (` +
              `${join(conflict.hourlyRates.map((rate) => formatAudExact(rate)))})`,
          )
          .join("; ") +
        ".",
    );
  }

  const negativeRates = analysis.grades.filter((g) => g.hourlyRate < 0);
  if (negativeRates.length > 0) {
    out.push(
      "These grades read as a negative hourly rate, which Morgan will not " +
        "store: " +
        join(
          negativeRates.map(
            (g) => `${g.name} (${formatAudExact(g.hourlyRate)})`,
          ),
        ) +
        ".",
    );
  }

  if (analysis.integrityFailures.length > 0) {
    const first = analysis.integrityFailures[0];
    out.push(
      `${rows(analysis.integrityFailureCount)} where net cost is not ` +
        `hours × rate — row ${first.rowNumber} works out to ` +
        `${formatAudExact(first.expectedCents / 100)} but the file says ` +
        `${formatAudExact(first.actualCents / 100)}. The rates read from this ` +
        "file cannot be trusted until that is explained.",
    );
  }

  // Two file grades on one existing grade would each write a rate at the same
  // effective date, so the second silently overwrites the first.
  for (const labels of clashingSelections(selections.grades)) {
    out.push(
      `${join(labels)} are matched to the same existing grade, so their ` +
        "rates would overwrite each other. Match them to different grades, or " +
        "create one of them.",
    );
  }

  for (const labels of clashingSelections(selections.staff)) {
    out.push(
      `${join(labels)} are matched to the same person, so only one of their ` +
        "grades would stick. Leave the duplicates unmatched.",
    );
  }

  return out;
}

/** Oddities worth reporting that phase 1 does not act on. */
export function collectAdvisories(analysis: WipAnalysis): string[] {
  const out: string[] = [];

  if (analysis.partitionFailures.length > 0) {
    out.push(
      `${rows(analysis.partitionFailureCount)} where write-off plus claim ` +
        `does not equal net cost (first: row ${analysis.partitionFailures[0]}). ` +
        "No money is imported in this step, so this does not stop the settings.",
    );
  }

  if (analysis.ipsHeadMismatches > 0) {
    out.push(
      `${rows(analysis.ipsHeadMismatches)} where the IPS column disagrees ` +
        "with the analysis code's prefix. Categories are taken from the " +
        "analysis code.",
    );
  }

  if (analysis.subAnalysisCodes.length > 0) {
    out.push(
      `This file also has a separate sub-analysis column with ` +
        `${analysis.subAnalysisCodes.length} value(s)` +
        (analysis.subAnalysisVariesWithinAnalysisCode
          ? ", which vary within a single analysis code"
          : "") +
        ". Sub-codes here are taken from the analysis code itself; that " +
        "column arrives with the time entries in a later step.",
    );
  }

  const withoutGrade = analysis.staff.filter((s) => !s.gradeName).length;
  if (withoutGrade > 0) {
    out.push(
      `${withoutGrade} staff name(s) have no grade in the file, so no grade ` +
        "can be assigned for them here.",
    );
  }

  // A grade only reaches the grades list when some row carried both its name
  // and a readable rate. Staff on a grade that never had one would otherwise be
  // matched on screen and then skipped at commit with nothing said.
  const ratedGrades = new Set(analysis.grades.map((g) => g.name));
  const unratedGrades = [
    ...new Set(
      analysis.staff
        .map((s) => s.gradeName)
        .filter((name) => name && !ratedGrades.has(name)),
    ),
  ];
  if (unratedGrades.length > 0) {
    out.push(
      `No hourly rate could be read for ${join(unratedGrades)}, so staff on ` +
        "those grades will be left without a grade even if you match them here.",
    );
  }

  return out;
}
